/**
 * Admin promo CRUD route. Plan §Promocodes (MVP scope: grant_credits only).
 *
 *   - List existing codes + redemptionsUsed counts
 *   - Create a new grant-credits code with optional expiry / cap / country segment
 *   - Deactivate (kill-switch) an active code
 */

import { useState } from 'react';
import {
  type PromoCodeRow,
  useCreatePromoCodeMutation,
  useDeactivatePromoCodeMutation,
  usePromoCodesQuery,
} from '../api/promo.js';

const LAUNCH_COUNTRY_HINT = 'PAK, IND, BGD, TUR, USA, GBR, …';

export function PromoRoute() {
  const codes = usePromoCodesQuery();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display font-semibold text-2xl text-neutral-900">Promo codes</h2>
        <button
          type="button"
          onClick={() => setShowCreate((s) => !s)}
          className="rounded-md bg-primary-600 px-4 py-2 font-medium text-sm text-white hover:bg-primary-700"
        >
          {showCreate ? 'Cancel' : '+ New code'}
        </button>
      </div>
      <p className="mb-6 text-neutral-600 text-sm">
        Grant-credits codes only at MVP. Each redemption appends a <code>promo_grant</code> entry to
        the credit ledger. One redemption per user. Deactivating a code is a kill-switch — existing
        redemptions are kept.
      </p>

      {showCreate ? <CreateForm onDone={() => setShowCreate(false)} /> : null}

      <section className="mt-6">
        {codes.isLoading ? (
          <p className="text-neutral-500 text-sm">Loading…</p>
        ) : codes.isError ? (
          <p className="text-danger-700 text-sm">Couldn't load codes.</p>
        ) : (codes.data?.codes ?? []).length === 0 ? (
          <p className="text-neutral-500 text-sm">No codes yet.</p>
        ) : (
          <ul className="space-y-3">
            {codes.data?.codes.map((c) => (
              <CodeCard key={c.code} code={c} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CodeCard({ code }: { code: PromoCodeRow }) {
  const deactivate = useDeactivatePromoCodeMutation();
  const expired = code.validUntil !== null && new Date(code.validUntil) < new Date();
  const capped = code.maxRedemptions !== null && code.redemptionsUsed >= code.maxRedemptions;

  return (
    <li className="rounded-md border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono font-semibold text-neutral-900">{code.code}</p>
          <p className="mt-1 text-neutral-500 text-xs">
            {code.grantCredits} credit{code.grantCredits === 1 ? '' : 's'} ·{' '}
            {code.active ? (
              <span className="text-success-700">active</span>
            ) : (
              <span className="text-neutral-500">inactive</span>
            )}
            {expired ? <span className="text-danger-700"> · expired</span> : null}
            {capped ? <span className="text-danger-700"> · capped</span> : null}
          </p>
          <p className="mt-0.5 text-neutral-500 text-xs">
            Redemptions: {code.redemptionsUsed}
            {code.maxRedemptions !== null ? ` / ${code.maxRedemptions}` : ' (uncapped)'}
          </p>
          <p className="mt-0.5 text-neutral-500 text-xs">
            Valid {new Date(code.validFrom).toLocaleDateString()} →{' '}
            {code.validUntil ? new Date(code.validUntil).toLocaleDateString() : 'no expiry'}
          </p>
          {code.segmentCountries && code.segmentCountries.length > 0 ? (
            <p className="mt-0.5 text-neutral-500 text-xs">
              Countries: {code.segmentCountries.join(', ')}
            </p>
          ) : null}
        </div>
        {code.active ? (
          <button
            type="button"
            onClick={() => deactivate.mutate(code.code)}
            disabled={deactivate.isPending}
            className="rounded-md border border-danger-300 bg-white px-3 py-1.5 font-medium text-danger-700 text-xs hover:bg-danger-50 disabled:opacity-60"
          >
            {deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const create = useCreatePromoCodeMutation();
  const [code, setCode] = useState('');
  const [grantCredits, setGrantCredits] = useState('5');
  const [validUntil, setValidUntil] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [segmentCountries, setSegmentCountries] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = () => {
    setErrors([]);
    const credits = Number.parseInt(grantCredits, 10);
    if (!code.trim()) {
      setErrors(['code is required']);
      return;
    }
    if (!Number.isFinite(credits) || credits <= 0) {
      setErrors(['grantCredits must be a positive integer']);
      return;
    }

    const countries =
      segmentCountries.trim().length > 0
        ? segmentCountries
            .split(',')
            .map((c) => c.trim().toUpperCase())
            .filter((c) => c.length === 3)
        : null;
    const maxRed = maxRedemptions.trim().length > 0 ? Number.parseInt(maxRedemptions, 10) : null;

    create.mutate(
      {
        code: code.trim(),
        grantCredits: credits,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        maxRedemptions: maxRed,
        segmentCountries: countries,
      },
      {
        onSuccess: (res) => {
          if (res.ok) {
            onDone();
          } else {
            setErrors([`rejected: ${res.reason}`]);
          }
        },
        onError: (err) => setErrors([String(err)]),
      },
    );
  };

  return (
    <div className="rounded-md border border-primary-200 bg-primary-50 p-4">
      <h3 className="mb-3 font-medium text-primary-900">New grant-credits code</h3>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-neutral-700 text-sm">Code</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 w-full rounded-md border border-neutral-200 p-2 font-mono text-sm uppercase"
              placeholder="WELCOME10"
            />
            <span className="mt-0.5 block text-neutral-500 text-xs">
              Normalized to uppercase server-side.
            </span>
          </label>
          <label className="block">
            <span className="text-neutral-700 text-sm">Grant credits</span>
            <input
              type="number"
              min={1}
              value={grantCredits}
              onChange={(e) => setGrantCredits(e.target.value)}
              className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-neutral-700 text-sm">Valid until (optional)</span>
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
            />
            <span className="mt-0.5 block text-neutral-500 text-xs">Empty = no expiry.</span>
          </label>
          <label className="block">
            <span className="text-neutral-700 text-sm">Max redemptions (optional)</span>
            <input
              type="number"
              min={1}
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
              placeholder="leave blank for uncapped"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-neutral-700 text-sm">Countries (optional)</span>
          <input
            type="text"
            value={segmentCountries}
            onChange={(e) => setSegmentCountries(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-200 p-2 text-sm"
            placeholder={LAUNCH_COUNTRY_HINT}
          />
          <span className="mt-0.5 block text-neutral-500 text-xs">
            Comma-separated ISO-3 codes. Empty = available in all launch countries.
          </span>
        </label>
        {errors.length > 0 ? (
          <ul className="rounded-md bg-danger-50 p-2 text-danger-700 text-xs">
            {errors.map((e) => (
              <li key={e}>· {e}</li>
            ))}
          </ul>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={create.isPending || !code}
            className="rounded-md bg-primary-600 px-4 py-2 font-medium text-sm text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
          <button
            type="button"
            onClick={onDone}
            className="rounded-md border border-neutral-200 bg-white px-4 py-2 font-medium text-neutral-700 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
